/**
 * Admin Authentication Hook (Story 7A.2)
 *
 * Provides admin authentication state by checking Firebase Auth
 * and the isAdmin custom claim in the user's ID token.
 *
 * FR59: Admin Panel Access
 * FR66: Admin Dashboard
 *
 * @module hooks/useAdminAuth
 */

import { useState, useEffect } from 'react';
import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from '../lib/firebase';

/**
 * Admin authentication state
 */
interface AdminAuthState {
  /** Current Firebase user or null if not authenticated */
  user: User | null;
  /** Whether user has isAdmin: true custom claim */
  isAdmin: boolean;
  /** Whether auth state is still being determined */
  loading: boolean;
}

/**
 * Hook to check admin authentication status
 *
 * Listens to Firebase Auth state changes and checks the isAdmin
 * custom claim in the user's ID token.
 *
 * @returns Admin authentication state
 *
 * @example
 * ```tsx
 * const { user, isAdmin, loading } = useAdminAuth();
 *
 * if (loading) return <Spinner />;
 * if (!isAdmin) return <Navigate to="/admin/login" />;
 * return <AdminDashboard />;
 * ```
 */
export function useAdminAuth(): AdminAuthState {
  const [state, setState] = useState<AdminAuthState>({
    user: null,
    isAdmin: false,
    loading: true,
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setState({ user: null, isAdmin: false, loading: false });
        return;
      }

      try {
        // Get token with claims
        const tokenResult = await user.getIdTokenResult();
        const isAdmin = tokenResult.claims.isAdmin === true;

        setState({ user, isAdmin, loading: false });
      } catch (error) {
        // If token fetch fails, treat as not admin
        console.error('Failed to get ID token:', error);
        setState({ user, isAdmin: false, loading: false });
      }
    });

    return () => unsubscribe();
  }, []);

  return state;
}
